import styled from '@emotion/styled';
import { tokens } from '@sunrun/experience-ui-components';
import { CountBadge } from 'components/atoms/CountBadge';

type UnderlinedTabGroupProps<T extends string> = {
  options: { label: string; value: T; badgeCount?: number }[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  size?: 'sm' | 'lg';
  justify?: 'center' | 'flex-start';
};

const UnderlinedTabGroup = <T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  size = 'sm',
  justify = 'center',
}: UnderlinedTabGroupProps<T>) => {
  return (
    <TabGroupContainer role="tablist" aria-label={ariaLabel} $justify={justify}>
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <TabButton
            key={option.value}
            type="button"
            role="tab"
            $active={isActive}
            aria-selected={isActive}
            onClick={() => onChange(option.value)}>
            <TabLabelRow>
              <TabLabel $active={isActive} $size={size}>
                {option.label}
              </TabLabel>
              {option.badgeCount != null && (
                <CountBadge badgeCount={option.badgeCount} />
              )}
            </TabLabelRow>
            <TabIndicator $active={isActive} $size={size} />
          </TabButton>
        );
      })}
    </TabGroupContainer>
  );
};

const TabGroupContainer = styled.div<{ $justify: 'center' | 'flex-start' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $justify }) => $justify};
  width: ${({ $justify }) => ($justify === 'flex-start' ? 'auto' : '100%')};
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 48px;
  padding: 8px 8px 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;

  &:focus-visible {
    outline: 2px solid ${tokens.BRAND_HEROBLUE};
    outline-offset: 2px;
  }
`;

const TabLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const TabLabel = styled.span<{ $active: boolean; $size: 'sm' | 'lg' }>`
  font-size: ${({ $size }) => ($size === 'lg' ? '18px' : '12px')};
  line-height: 1.2;
  letter-spacing: ${({ $size }) => ($size === 'lg' ? '-0.36px' : '0.24px')};
  white-space: nowrap;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${({ $active, $size }) => {
    if ($size === 'lg') {
      return $active ? tokens.BRAND_HERO_BLUE : tokens.BRAND_MEDIUM_BLUE;
    }
    return $active ? tokens.BRAND_HEROBLUE : tokens.BRAND_MEDIUM_BLUE;
  }};
`;

const TabIndicator = styled.div<{ $active: boolean; $size: 'sm' | 'lg' }>`
  width: 100%;
  height: 3px;
  border-radius: 4px;
  background-color: ${({ $active, $size }) => {
    if (!$active) {
      return 'transparent';
    }
    return $size === 'lg' ? tokens.BRAND_HERO_BLUE : tokens.BRAND_HEROBLUE;
  }};
`;

export { UnderlinedTabGroup };
